import { FormattedMessage } from 'react-intl';
import { LeftSide, RightSide } from './Shared';
import { addBusinessDays } from 'date-fns';
import { isDefined, isNotDefined } from '@sgme/fp';
import { formatDateLocalized } from '@/utils/locale';
import { toUtc } from '@/utils/dates';
import { DATE_FULL_FORMAT } from '@/utils/format';
import type { Currency } from '@/models/currency';

export type Product =
  | {
      name: 'FxSpot';
      spotRate: string;
      sellAmount: number;
      sellCurrency: Currency;
      buyAmount: number;
      buyCurrency: Currency;
    }
  | {
      name: 'FxFwd';
      spotRate: string;
      forwardPoints: string;
      forwardRate: string;
      sellAmount: number;
      sellCurrency: Currency;
      buyAmount: number;
      buyCurrency: Currency;
    }
  | {
      name: 'FxPredeliver';
      spotRate: string;
      predeliverPoints: string;
      predeliverRate: string;
      sellAmount: number;
      sellCurrency: Currency;
      buyAmount: number;
      buyCurrency: Currency;
      masterAmount: 'buyAmount' | 'sellAmount';
    }
  | {
      name: 'FxRollover';
      spotRate: string;
      rolloverPoints: string;
      rolloverRate: string;
      sellAmount: number;
      sellCurrency: Currency;
      buyAmount: number;
      buyCurrency: Currency;
      masterAmount: 'buyAmount' | 'sellAmount';
    }
  | {
      name: 'FxAmericanForward';
      americanForwardRate: string;
      callabilityStart: string | undefined;
      sellAmount: number;
      sellCurrency: Currency;
      buyAmount: number;
      buyCurrency: Currency;
    };

export function ProductDetails({
  rfsDate,
  product,
  initialDate,
  formatAmountWithPrecision,
}: { rfsDate: Date; product: Product; initialDate?: string; formatAmountWithPrecision: (amount: number, precision: number) => string }) {
  const date = isDefined(rfsDate) ? formatDateLocalized(toUtc(new Date(rfsDate)), DATE_FULL_FORMAT) : null;
  const initialFormattedDate = isDefined(initialDate) ? formatDateLocalized(toUtc(new Date(initialDate)), DATE_FULL_FORMAT) : null;

  const isForward = new Date(rfsDate) > addBusinessDays(new Date(), 2);

  if (isNotDefined(date)) {
    return null;
  }

  switch (product.name) {
    case 'FxFwd':
    case 'FxSpot':
      return (
        <>
          <LeftSide>
            <FormattedMessage id={`${isForward ? 'trader.successModal.productDetails.forwardRate' : 'trader.successModal.productDetails.spotRate'}`} />
          </LeftSide>
          <RightSide dataE2E="product-details-spot-rate">{isForward && product.name === 'FxFwd' ? product.forwardRate : product.spotRate}</RightSide>
          <ContraAmount amount={formatAmountWithPrecision(product.sellAmount, 2)} currency={product.sellCurrency} />
          <LeftSide>
            <FormattedMessage id="trader.successModal.productDetails.spotDate" />
          </LeftSide>
          <RightSide dataE2E="forward-date">{date}</RightSide>
        </>
      );

    case 'FxPredeliver':
      return (
        <>
          <ContraAmount amount={formatAmountWithPrecision(product.sellAmount, 2)} currency={product.sellCurrency} />
          <LeftSide>
            <FormattedMessage id="trader.successModal.productDetails.predeliverRate" />
          </LeftSide>
          <RightSide>{product.predeliverRate}</RightSide>
          <LeftSide>
            <FormattedMessage id="trader.successModal.productDetails.predeliverSpotRate" />
          </LeftSide>
          <RightSide>
            <span>{product.spotRate}</span>
          </RightSide>
          <LeftSide>
            <FormattedMessage id="trader.successModal.productDetails.predeliverPoints" />
          </LeftSide>
          <RightSide>
            <span>{product.predeliverPoints}</span>
          </RightSide>
          <LeftSide>
            <FormattedMessage id="trader.successModal.productDetails.predeliverDate" />
          </LeftSide>
          <RightSide>{date}</RightSide>
        </>
      );
    case 'FxRollover':
      return (
        <>
          <LeftSide>
            <FormattedMessage id="trader.successModal.productDetails.rolloverContramount" />
          </LeftSide>
          <RightSide>
            <FormattedMessage id="trader.successModal.sell" />
            &nbsp;
            <span data-e2e="sell-amount">
              {product.masterAmount === 'buyAmount' ? formatAmountWithPrecision(product.buyAmount, 2) : formatAmountWithPrecision(product.sellAmount, 2)}
            </span>
            <span data-e2e="sell-currency"> {product.masterAmount === 'buyAmount' ? product.buyCurrency : product.sellCurrency}</span>
          </RightSide>
          <LeftSide>
            <FormattedMessage id="trader.successModal.productDetails.rolloverNewDate" />
          </LeftSide>
          <RightSide>{date}</RightSide>
          <LeftSide>
            <FormattedMessage id="trader.successModal.productDetails.rolloverInitialDate" />
          </LeftSide>
          <RightSide>{initialFormattedDate}</RightSide>
          <LeftSide>
            <FormattedMessage id="trader.successModal.productDetails.rolloverRate" />
          </LeftSide>
          <RightSide>{product.rolloverRate}</RightSide>
          <LeftSide>
            <FormattedMessage id="trader.successModal.productDetails.rolloverSpotRate" />
          </LeftSide>
          <RightSide dataE2E="product-details-spot-rate">{product.spotRate}</RightSide>
        </>
      );
    case 'FxAmericanForward':
      return (
        <>
          <LeftSide>
            <FormattedMessage id="trader.successModal.operation.americanForward" />
          </LeftSide>
          <RightSide>
            <FormattedMessage id="trader.successModal.sell" />
            &nbsp;
            <span data-e2e="buy-amount">{product.sellAmount}</span>&nbsp;
            <span data-e2e="buy-currency">{product.sellCurrency}</span>
          </RightSide>

          <LeftSide>
            <FormattedMessage id="trader.successModal.productDetails.americanForwardRate" />
          </LeftSide>
          <RightSide>{product.americanForwardRate}</RightSide>
          <LeftSide>
            <FormattedMessage id="trader.successModal.contramount" />
          </LeftSide>

          <RightSide>
            <FormattedMessage id="trader.successModal.buy" />
            &nbsp;
            <span data-e2e="sell-amount">{product.buyAmount}</span>&nbsp;
            <span data-e2e="sell-currency"> {product.buyCurrency}</span>
          </RightSide>

          <LeftSide>
            <FormattedMessage id="trader.successModal.productDetails.spotDate" />
          </LeftSide>
          <RightSide dataE2E="forward-date">{date}</RightSide>

          <LeftSide>
            <FormattedMessage id="trader.successModal.productDetails.callabilityStart" />
          </LeftSide>
          <RightSide dataE2E="callabilityStart-date">
            {isDefined(product.callabilityStart) && formatDateLocalized(toUtc(new Date(product.callabilityStart)), DATE_FULL_FORMAT)}
          </RightSide>
        </>
      );
    default: {
      const exhaustiveCheck: never = product;
      return exhaustiveCheck;
    }
  }
}

const ContraAmount = ({ amount: sellAmount, currency: sellCurrency }: { amount: string; currency: Currency }) => {
  return (
    <>
      <LeftSide>
        <FormattedMessage id="trader.successModal.contramount" />
      </LeftSide>

      <RightSide>
        <FormattedMessage id="trader.successModal.sell" />
        &nbsp;
        <span data-e2e="sell-amount">{sellAmount}</span>
        <span data-e2e="sell-currency"> {sellCurrency}</span>
      </RightSide>
    </>
  );
};
